'use client'

import React, { useState } from 'react'
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react'

type Project = {
  id: number
  nom: string
}

type Props = {
  open: boolean
  onClose: () => void
  projects: Project[]
  onProjectSelect: (project: Project) => void
  onProjectCreate: (project: Project) => void
}

export default function ProjectCreationModal({
  open,
  onClose,
  projects,
  onProjectSelect,
  onProjectCreate,
}: Props) {
  const [search, setSearch] = useState('')
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [creating, setCreating] = useState(false)

  const filteredProjects = projects.filter(p =>
    p.nom.toLowerCase().includes(search.trim().toLowerCase())
  )

  const handleCreate = async () => {
    if (!search.trim()) return
    setCreating(true)
    try {
      const res = await fetch('/api/projets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nom: search.trim().toUpperCase() }),
      })
      if (!res.ok) throw new Error()
      const created = await res.json()
      onProjectCreate(created)
      onClose()
      setSearch('')
    } catch {
      alert('Erreur lors de la création du projet')
    } finally {
      setCreating(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} className="fixed z-50 inset-0 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-black opacity-30" aria-hidden="true" />
        <DialogPanel className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 z-10 relative">
          <DialogTitle className="text-lg font-bold mb-4">Projet</DialogTitle>
          <div className="relative">
            <label className="block text-sm font-medium mb-1">Rechercher ou créer</label>
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setDropdownOpen(true)
              }}
              onFocus={() => setDropdownOpen(true)}
              onBlur={() => setTimeout(() => setDropdownOpen(false), 150)}
              placeholder="Tapez pour chercher..."
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition"
              autoFocus
            />
            {dropdownOpen && (
              <ul className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg py-1 animate-fade-in max-h-64 overflow-y-auto" role="listbox">
                {filteredProjects.length > 0 ? (
                  filteredProjects.map((p) => (
                    <li
                      key={p.id}
                      className="px-4 py-2 cursor-pointer transition rounded-lg mx-1 my-1 hover:bg-blue-50 hover:text-blue-700"
                      onMouseDown={() => {
                        onProjectSelect(p)
                        onClose()
                        setSearch('')
                      }}
                      role="option"
                      aria-selected={false}
                    >
                      {p.nom}
                    </li>
                  ))
                ) : (
                  search.trim() && (
                    <li
                      className="px-4 py-2 cursor-pointer transition rounded-lg mx-1 my-1 text-blue-700 hover:bg-blue-50"
                      onMouseDown={handleCreate}
                    >
                      Créer « {search.trim().toUpperCase()} »
                    </li>
                  )
                )}
              </ul>
            )}
          </div>
          <div className="flex justify-end mt-4">
            <button type="button" className="px-4 py-2 bg-gray-200 rounded" onClick={onClose}>
              Fermer
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  )
}
